import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, TextField, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { QualificationType, QualificationTypeString, qTabs } from '@AMIEWEB/NewOrder/constants';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { IDetails } from '@AMIEWEB/NewOrder/types';
import { Concatenate } from 'utils/string/string';

export const getCharacterCount = (validCharacterCount, count, t) => {
  const helperDiv = (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      {validCharacterCount && count?.length >= 8000 ? <span>{t}</span> : <span style={{ visibility: 'hidden' }} />}
      <span>{`${count?.length ?? 0}/8000`}</span>
    </div>
  );
  return helperDiv;
};

const useStyles = makeStyles<{ expanded: boolean; isEditQualification: boolean; orderCreationEdit: boolean }>()(
  (theme, { expanded, isEditQualification, orderCreationEdit }) => ({
    dialog: {
      minWidth: '960px',
      '& .MuiDialog-paperWidthSm': {
        minWidth: '900px',
        maxWidth: '100%',
      },
    },
    content: {
      height: '453px',
      overflowX: 'hidden',
      width: '100%',
    },
    indicator: {
      width: '5px',
      left: '0px',
      background: theme.palette.framework.system.violet,
    },
    filterTabs: {
      background: theme.palette.framework.system.whisper,
      height: !expanded ? '100%' : '453px',
      width: '225px',
      minWidth: '225px',
    },
    tab: {
      marginLeft: '10px',
      textTransform: 'none',
      color: theme.palette.framework.system.doveGray,
      alignItems: 'flex-start !important',
      justifyContent: 'flex-start',
      '& .MuiTab-wrapper': {
        alignItems: 'flex-start !important',
        justifyContent: 'flex-start',
      },
      '&.Mui-selected': {
        color: theme.palette.framework.system.violet,
      },
      textAlign: 'start',
      textWrap: 'wrap',
      height: 'auto',
    },
    container: {
      height: !expanded ? '100%' : '453px',
      width: '100%',
      display: 'flex',
    },
    form: {
      padding: '24px 24px 0px 24px',
      ...(!isEditQualification && !orderCreationEdit && expanded && { width: '600px' }),
    },
    dialogueContent: {
      padding: 0,
      '& .MuiDialogContent-root': {
        padding: 0,
      },
      '& .dialogContentRoot': {
        padding: 0,
      },
      overflow: 'hidden',
    },
    fullScreenContainer: {
      height: '100%',
      overflow: 'hidden',
      overflowY: 'scroll',
      width: '100%',
    },
    filterTabsExpanded: {
      background: theme.palette.framework.system.whisper,
      width: '225px',
      minWidth: '225px',
    },
    notes: {
      backgroundColor: theme.palette.system.white,
      marginTop: '1%',
      marginBottom: '8px',
      width: '100%',
      '& .MuiInputBase-root': {
        width: '100%',
        height: 'auto',
        display: 'inline-flex',
        flexDirection: 'column',
        margin: '2px',
      },
      '& .MuiOutlinedInput-root': {
        margin: '2px',
        position: 'initial',
      },
      '& label': {
        '&.Mui-focused': {
          color: theme.palette.system.dimGrey2,
        },
      },
    },
  }),
);

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

interface IQualificationModalEnhancedProps {
  open: boolean;
  title: string;
  onClose: (event: React.MouseEvent<HTMLButtonElement>, reason: string) => void;
  isNewOrderCreation: boolean;
  details: IDetails;
  isEditQualification: boolean;
  orderCreationEdit: boolean;
}
const QualificationModalEnhanced = (props: IQualificationModalEnhancedProps) => {
  const { open, title, isNewOrderCreation, details, onClose, isEditQualification, orderCreationEdit } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { classes } = useStyles({ expanded, isEditQualification, orderCreationEdit });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [validCharacterCount, setValidCharacterCount] = React.useState({});

  const handleCharacterCount = (field, value) => {
    setValidCharacterCount(prevCounts => ({
      ...prevCounts,
      [field]: value?.length >= 8000,
    }));
  };

  const [qualificationTexts, setQualificationTexts] = useState({
    requiredQualifications: '',
    preferredQualifications: '',
    additionalRequiredQualifications: '',
    internalNotes: '',
    jobDescription: '',
    ...details.orderDetails,
  });

  const dispatch = useDispatch();
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const ElementId = qTabs[newValue].id;
    const element = document.getElementById(ElementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setSelectedTab(newValue);
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const updatedDetails = { ...details.orderDetails };

    qTabs.forEach(tab => {
      updatedDetails[tab.id] = qualificationTexts[tab.id];
    });

    if (isNewOrderCreation) {
      qTabs.forEach(tab => {
        dispatch(orderCreationNewActions.setQualificationText({ name: tab.id, value: qualificationTexts[tab.id] }));
      });

      dispatch(
        orderCreationNewActions.setQualificationText({
          name: QualificationTypeString[QualificationType.Required],
          value: details?.orderDetails?.additionalRequiredQualifications
            ? qualificationTexts?.requiredQualifications?.includes(
                details?.orderDetails?.additionalRequiredQualifications,
              )
              ? qualificationTexts?.requiredQualifications?.replace(
                  details?.orderDetails?.additionalRequiredQualifications,
                  `\n${qualificationTexts?.additionalRequiredQualifications}`,
                )
              : Concatenate(
                  [
                    qualificationTexts?.requiredQualifications?.trim(),
                    `\n${qualificationTexts?.additionalRequiredQualifications}`,
                  ],
                  '\n',
                )
            : Concatenate(
                [
                  qualificationTexts?.requiredQualifications?.trim(),
                  `\n${qualificationTexts?.additionalRequiredQualifications}`,
                ],
                '\n',
              ),
        }),
      );
    } else {
      dispatch(
        orderCreationNewActions.onEditQualifications({
          ...details?.orderDetails,
          [QualificationTypeString[QualificationType.Required]]:
            !!qualificationTexts?.additionalRequiredQualifications && qualificationTexts?.additionalRequiredQualifications?.trim() !== ''
              ? Concatenate(
                  [
                    qualificationTexts?.requiredQualifications ?? '',
                    `\n${qualificationTexts?.additionalRequiredQualifications}`,
                  ],
                  '\n',
                )
              : qualificationTexts?.requiredQualifications ?? '',
          [QualificationTypeString[QualificationType.AdditionalRequiredQualifications]]:
            qualificationTexts?.additionalRequiredQualifications,
          [QualificationTypeString[QualificationType.Preferred]]: qualificationTexts?.preferredQualifications,
          [QualificationTypeString[QualificationType.InternalNotes]]: qualificationTexts?.internalNotes,
          [QualificationTypeString[QualificationType.ExternalJobDetails]]: qualificationTexts?.jobDescription,
        }),
      );
    }

    onClose(event, 'submit');
  };

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleTextChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = e.target.value;
    if (value.length <= 8000) {
      setQualificationTexts({ ...qualificationTexts, [field]: value });
      handleCharacterCount(field, value);
    }
  };
  const handlePaste = (field: string) => (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    let paste = e.clipboardData.getData('text');
    const currentValue = qualificationTexts[field] || '';

    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const cursorPosition = target.selectionStart;
    const textBeforeCursor = currentValue.substring(0, cursorPosition);
    const textAfterCursor = currentValue.substring(cursorPosition);

    let newTextLength = textBeforeCursor.length + paste.length + textAfterCursor.length;
    let charactersToPaste = paste;

    if (newTextLength > 8000) {
      const allowedChars = 8000 - textBeforeCursor.length - textAfterCursor.length;
      charactersToPaste = paste.slice(0, allowedChars);
    }

    const newValue = textBeforeCursor + charactersToPaste + textAfterCursor;

    setQualificationTexts({ ...qualificationTexts, [field]: newValue });
    handleCharacterCount(field, newValue);
  };

  const removeAdditionalQualifications = (required: string, additional: string): string => {
    if (!required || !additional) return required;
    const escapedAdditional = additional.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const additionalWithNewlineRegex = new RegExp(`(^|\\s)${escapedAdditional}($|\\s)`, 'g');
    const hasMatch = additionalWithNewlineRegex.test(required);
    if (hasMatch) {
      return required.replace(additionalWithNewlineRegex, '').trim();
    } else {
      return required;
    }
  };

  useEffect(() => {
    const initialTexts = {
      requiredQualifications: details.orderDetails.requiredQualifications?.includes(
        details.orderDetails.additionalRequiredQualifications,
      )
        ? removeAdditionalQualifications(
            details.orderDetails.requiredQualifications,
            details.orderDetails.additionalRequiredQualifications,
          )
        : details.orderDetails.requiredQualifications || '',
      preferredQualifications: details.orderDetails.preferredQualifications || '',
      additionalRequiredQualifications: details.orderDetails.additionalRequiredQualifications || '',
      internalNotes: details.orderDetails.internalNotes || '',
      jobDescription: details.orderDetails.jobDescription || '',
    };
    setQualificationTexts(initialTexts);
    Object.entries(initialTexts)?.forEach(([field, value]) => {
      handleCharacterCount(field, value);
    });
  }, [details.orderDetails]);

  useEffect(() => {
    const handleScroll = (newValue: number) => {
      const ElementId = qTabs[newValue]?.id;
      if (ElementId) {
        const element = document.getElementById(ElementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    handleScroll(selectedTab);
  }, [selectedTab]);

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      variant="blue"
      className={classes.dialog}
      dialogTitleProps={{
        text: title,
        closeButton: true,
        expandable: true,
        onExpandChange: handleExpanded,
      }}
      dialogActions={
        !isEditQualification && !orderCreationEdit
          ? []
          : [
              {
                text: t('order.facility.creditHoldDialog.cancelBtn'),
                variant: 'contained',
                color: 'tertiary',
                onClick: e => onClose(e, ''),
              },
              {
                text: t('candidate.skillsets.save'),
                variant: 'contained',
                color: 'primary',
                onClick: e => handleSubmit(e),
              },
            ]
      }
      dialogContentProps={{
        classes: {
          root: classes.dialogueContent,
        },
      }}
    >
      <div className={classes.container}>
        <Box className={expanded ? classes.filterTabsExpanded : classes.filterTabs}>
          <Box style={{ marginTop: '20px' }}>
            <Tabs
              value={selectedTab}
              classes={{
                indicator: classes.indicator,
              }}
              textColor="primary"
              orientation="vertical"
              onChange={handleChange}
              aria-label="edit product selector"
            >
              {qTabs.map((tab, index) => (
                <Tab key={index} label={t(`${tab.label}`)} className={classes.tab} id={tab.id} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </Box>
        <div className={expanded ? classes.content : classes.fullScreenContainer}>
          <div className={classes.form}>
            <Box>
              <div id="qualifications">
                {qTabs.map((tab, index) => (
                  <Box key={index} id={tab.id}>
                    <Typography variant="h6">{tab.label}</Typography>
                    {!isEditQualification && !orderCreationEdit ? (
                      <Typography className={classes.notes}>{qualificationTexts[tab?.id]}</Typography>
                    ) : (
                      <TextField
                        className={classes.notes}
                        variant="filled"
                        multiline
                        value={qualificationTexts[tab?.id]}
                        onChange={handleTextChange(tab?.id)}
                        onPaste={handlePaste(tab?.id)}
                        error={qualificationTexts[tab?.id]?.length >= 8000}
                        helperText={getCharacterCount(
                          validCharacterCount[tab?.id],
                          qualificationTexts[tab?.id],
                          t('order.maxCharLimit'),
                        )}
                      />
                    )}
                  </Box>
                ))}
              </div>
            </Box>
          </div>
        </div>
      </div>
    </GenericDialog>
  );
};

export default QualificationModalEnhanced;
